import React, { useMemo, useState } from 'react';
import { Edit, SettingIcon, ViewIcon } from '../../../assets/svg';
import { isAdminRole, LOCAL_STORAGE, ROLES, ROUTES } from '../../../constants';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { HideIcon } from '../../../assets/svg/HideIcon';
import { getLocalStorageItem, popup, updateCSMStatus } from '../../../utils';
import { ConfirmationBox } from '../../shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../../constants/queryKey';
import { Modal } from 'react-bootstrap';
import NotificationSettings from '../../shared/common/NotificationSettings';

export const CsmProfile = ({ id, status, full_name, role_code }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [openUserSetting, setOpenUserSetting] = useState(false);
    const role = useSelector((s) => s.user.role);
    const loggedInUserId = getLocalStorageItem(LOCAL_STORAGE.USER_ID);
    const isEdit = useMemo(() => [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(role), [role]);
    const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
    const handleEditClick = () => {
        navigate(ROUTES.ADMIN.EDIT_PROFILE, {
            state: {
                userId: id,
            },
        });
    };

    const deactivateUserMutation = useMutation({
        mutationFn: async () => {
            await updateCSMStatus({ ids: [id], status: status === 1 ? 0 : 1 });
        },
        onSuccess: () => {
            popup(
                'success',
                `${full_name}'s account has been ${!status === 1 ? 'activated' : 'deactivated'}`,
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS], exact: false });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INACTIVE_USERS], exact: false });
        },
        onError: (error) => {
            popup('error', error.message);
        },
    });

    return (
        <div className='d-flex align-items-center justify-content-center options-set'>
            <button
                className={`border-0 p-0 bg-white`}
                onClick={() => handleEditClick()}
                id='action-btn'
                title={isEdit ? 'Edit' : 'View'}
            >
                {isEdit ? <Edit /> : <ViewIcon />}
            </button>
            {isEdit && (
                <button
                    className={`border-0 p-0 bg-white`}
                    onClick={() => setOpenConfirmationBox(true)}
                    id='action-btn'
                    title={'Deactivate'}
                >
                    <HideIcon />
                </button>
            )}
            {isAdminRole(role) && role_code === ROLES.CSM && (
                <button
                    title={'CSM Settings'}
                    id='action-btn'
                    type='button'
                    onClick={() => setOpenUserSetting(true)}
                    aria-label={`${full_name}'s Email Notification Settings`}
                >
                    <SettingIcon />
                </button>
            )}
            {openConfirmationBox && (
                <ConfirmationBox
                    setConfirm={(v) => {
                        if (v) {
                            deactivateUserMutation.mutate();
                        }
                    }}
                    openConfirmationBox={openConfirmationBox}
                    setOpenConfirmationBox={setOpenConfirmationBox}
                    customMessage={`Are you sure you want to ${status === 1 ? 'deactivate' : 'activate'} ${full_name}?`}
                />
            )}
            {openUserSetting && isAdminRole(role) && role_code === ROLES.CSM && (
                <Modal
                    show={openUserSetting}
                    onHide={() => {
                        setOpenUserSetting(false);
                    }}
                    size='md'
                    dialogClassName='overflow-hidden'
                    centered
                >
                    <Modal.Header closeButton>
                        <Modal.Title style={{ fontSize: 'var(--font-18)' }}>
                            CSM Notification Preferences
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <NotificationSettings
                            id={id}
                            disabled={!isAdminRole(role) && id !== loggedInUserId}
                        />
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};
